/**
 * Point-sell (branch) rules shared by the store and the rental views.
 *
 * MOB-043: "is this branch bookable online?" used to be decided in four places — the
 * `loadPointSells` filter, the two rental view guards (each re-deriving the rule as "is this
 * id in the already filtered list"), and `getOrderr`, which did not check at all, so an order
 * restored from the cookie could make a rent-disabled branch the active one. The rule now
 * lives here and nowhere else.
 *
 * The comparisons below are the ones the code being replaced made, kept character for
 * character on purpose:
 *
 *   - the disabled-list membership test is `Array.prototype.includes`, i.e. the same
 *     SameValueZero comparison as `rentDisabledPointSells.includes(ps.mssId)` in
 *     `loadPointSells`. A hand-authored `"7"` in `mobConfig.rentDisabledPointSells` does NOT
 *     match a numeric `mssId` of 7 — exactly as today. Normalizing the two would silently
 *     remove a branch that is live and bookable from every tenant configured that way, which
 *     is a production behavior change no issue has asked for. If that normalization is
 *     wanted, it needs its own ticket and its own release note.
 *
 *   - the lookup by route parameter is a loose `==`, i.e. the same comparison as
 *     `pointSells.find(p => p.mssId == pointSellMssId)`. `/rent/search/01` keeps resolving to
 *     `mssId` 1.
 */

/**
 * The `mssId` of a branch, whether given the branch object or the id itself.
 *
 * @param {object|number|string|null} pointSellOrMssId - branch object or its mssId
 * @returns {number|string|null|undefined} the mssId as it is stored, unconverted
 */
function mssIdOf(pointSellOrMssId) {
  if (pointSellOrMssId != null && typeof pointSellOrMssId === "object") {
    return pointSellOrMssId.mssId;
  }
  return pointSellOrMssId;
}

/**
 * Whether a branch is named in `mobConfig.rentDisabledPointSells`.
 *
 * This is a POSITIVE match, and deliberately so: the answer drives a destructive path
 * (`getOrderr` refuses the branch and offers to drop the client's cart), so only an id that
 * actually appears in the configured list may trigger it. A branch whose `mssId` is missing
 * is therefore disabled only by a list that literally contains that missing value — which is
 * exactly what `!rentDisabledPointSells.includes(ps.mssId)` did with such a branch, and the
 * point of keeping this a plain `includes` is that the filter and every guard stay one rule.
 *
 * @param {object|number|string|null} pointSellOrMssId - branch object or its mssId
 * @param {Array<number|string>} [rentDisabledPointSells] - `mobConfig.rentDisabledPointSells`
 * @returns {boolean} true when the branch is named in the disabled list
 */
export function isPointSellRentDisabled(pointSellOrMssId, rentDisabledPointSells = []) {
  const disabled = Array.isArray(rentDisabledPointSells) ? rentDisabledPointSells : [];
  return disabled.includes(mssIdOf(pointSellOrMssId));
}

/**
 * Whether a branch may be used for online rental — the negation of
 * {@link isPointSellRentDisabled}, and the form the call sites read best.
 *
 * @param {object|number|string|null} pointSellOrMssId - branch object or its mssId
 * @param {Array<number|string>} [rentDisabledPointSells] - `mobConfig.rentDisabledPointSells`
 * @returns {boolean} true when the branch is bookable online
 */
export function isPointSellRentable(pointSellOrMssId, rentDisabledPointSells = []) {
  return !isPointSellRentDisabled(pointSellOrMssId, rentDisabledPointSells);
}

/**
 * The branch with this `mssId` from a list, but only when it is rentable.
 *
 * Both rental view guards resolve a branch out of `state.pointSells` by the route's
 * `pointSellMssId`, which is a string — hence the loose `==`, unchanged from the two loops
 * this replaces. The list is already filtered by `loadPointSells` with the same predicate, so
 * the rentable check cannot reject anything today; it is what makes the guards agree with the
 * filter if a list is ever read that was loaded before the branch was disabled.
 *
 * @param {Array<object>} pointSells - loaded branches
 * @param {number|string|null} mssId - the id to look up
 * @param {Array<number|string>} [rentDisabledPointSells] - `mobConfig.rentDisabledPointSells`
 * @returns {object|null} the branch, or null when absent or rent-disabled
 */
export function findRentablePointSell(pointSells, mssId, rentDisabledPointSells = []) {
  const list = Array.isArray(pointSells) ? pointSells : [];
  // The loose == is deliberate: it is the comparison the two view guards made before.
  const found = list.find((ps) => ps != null && ps.mssId == mssId);

  return found != null && isPointSellRentable(found, rentDisabledPointSells)
    ? found
    : null;
}

export default { isPointSellRentDisabled, isPointSellRentable, findRentablePointSell };
